import { getProcessInstances } from '@/api/backend/workflow';
import { ProcessInstanceResponseType } from '@/api/backend/types';
import { ProcessStatusBadge } from '@/components/process-status-badge';
import { ResponsiveDataView } from '@/components/responsive-data-view';
import { PROCESS_BUSINESS_STATUSES } from '@/lib/constants';
import { formatIsoDate } from '@/lib/utils';
import {
  Card,
  DataTable,
  DataTableColumnType,
  DataTableFilterConfig,
  DataTableSortConfig,
  Link,
  SearchInput,
} from '@opaz/ui-components';
import { useQuery } from '@tanstack/react-query';

import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 10;

export function AllProcessesCard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [filterConfig, setFilterConfig] = useState<DataTableFilterConfig>({});
  const [sortConfig, setSortConfig] = useState<DataTableSortConfig | null>(null);
  const [tempSearchValue, setTempSearchValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const { data: page, isLoading: isApplicationsLoading } = useQuery({
    queryKey: ['applications', currentPage, filterConfig, sortConfig, searchValue],
    queryFn: () => {
      const status = filterConfig.businessStatus?.value as string;

      return getProcessInstances(
        {
          start: currentPage * PAGE_SIZE,
          size: PAGE_SIZE,
        },
        sortConfig,
        { status, searchTerm: searchValue || undefined },
      );
    },
  });

  const columns: DataTableColumnType<ProcessInstanceResponseType>[] = [
    {
      title: t('dashboard.applications.headings.id'),
      render: (record: ProcessInstanceResponseType) => (
        <Link
          onClick={() => {
            navigate({
              to: `/processes/${record.id}`,
              params: { processInstanceId: record.id },
            });
          }}
          className="w-[120px] text-start"
        >
          {record.businessKey}
        </Link>
      ),
    },
    {
      title: t('dashboard.applications.headings.applicationName'),
      render: (record: ProcessInstanceResponseType) => (
        <div className="flex flex-col gap-1">
          <div className="text-[#132459] font-bold text-base">
            {record.translations?.[i18n.language]?.processDefinitionName ?? record.processDefinitionName}
          </div>
        </div>
      ),
    },
    {
      title: t('dashboard.applications.headings.zone'),
      render: (record: ProcessInstanceResponseType) => {
        const zoneCode = record.context?.zoneCode ?? record.parent?.context?.zoneCode ?? record.root?.context?.zoneCode;
        return zoneCode ? t(`common.zoneCode.${zoneCode}`) : t('common.unknown');
      },
    },
    {
      title: t('dashboard.applications.headings.submitted'),
      sortKey: 'startTime',
      render: (record: ProcessInstanceResponseType) => formatIsoDate(record.startTime, i18n.language),
    },
    {
      title: t('dashboard.applications.headings.status'),
      align: 'end',
      filter: {
        type: 'select',
        key: 'businessStatus',
        options: PROCESS_BUSINESS_STATUSES.map((status) => ({
          label: t(`processInstance.status.${status}`),
          value: status,
        })),
      },
      render: (record: ProcessInstanceResponseType) => {
        return (
          <div className="flex flex-col items-end gap-1">
            <ProcessStatusBadge businessStatus={record.businessStatus} completed={record.completed} />
          </div>
        );
      },
    },
  ];

  return (
    <Card className="w-full p-5 shadow-lg border-none">
      <h2 className="text-2xl font-semibold mb-6">{t('dashboard.applications.title')}</h2>

      <SearchInput
        className="mb-5 max-w-md"
        onChange={(e) => {
          setTempSearchValue(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setSearchValue(tempSearchValue);
            setCurrentPage(0);
          }
        }}
        value={tempSearchValue || ''}
        onClear={() => {
          setTempSearchValue('');
          setSearchValue('');
          setCurrentPage(0);
        }}
        onSearchSubmit={() => {
          setSearchValue(tempSearchValue);
          setCurrentPage(0);
        }}
      />

      <ResponsiveDataView
        columns={columns}
        data={page?.data?.length ? [...page.data] : []}
        isLoading={isApplicationsLoading}
        emptyMessage={t('common.table.emptyState')}
        currentPage={currentPage + 1}
        totalPages={page?.total ? Math.ceil(page.total / PAGE_SIZE) : 0}
        onPageChange={(page) => setCurrentPage(page - 1)}
        getRowKey={(record) => record.id}
        filterConfig={filterConfig}
        onFilterChange={(config) => {
          setCurrentPage(0);
          setFilterConfig(config);
        }}
      >
        <DataTable
          columns={columns}
          dataSource={page?.data?.length ? [...page.data] : []}
          isLoading={isApplicationsLoading}
          currentPage={currentPage + 1}
          totalPages={page?.total ? Math.ceil(page.total / PAGE_SIZE) : 0}
          onPageChange={(page) => setCurrentPage(page - 1)}
          onFilterChange={(config) => {
            setCurrentPage(0);
            setFilterConfig(config);
          }}
          onSortChange={setSortConfig}
          translations={{
            apply: t('common.table.filter.apply'),
            clear: t('common.table.filter.clear'),
            from: t('common.table.filter.from'),
            clearAllFilters: t('common.table.filter.clearAllFilters'),
            noData: t('common.table.emptyState'),
            nextPage: t('common.table.next'),
            previousPage: t('common.table.previous'),
          }}
        />
      </ResponsiveDataView>
    </Card>
  );
}
